import { useState, useEffect, useCallback } from 'react';
import { getAdminProfileUpdateRequests, getAdminProfileUpdateRequest, approveProfileUpdateRequest, rejectProfileUpdateRequest } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const statusStyle = {
  pending:  { color: '#ffaa44', bg: 'rgba(255,170,68,0.12)', border: 'rgba(255,170,68,0.3)' },
  approved: { color: '#66ff99', bg: 'rgba(102,255,153,0.12)', border: 'rgba(102,255,153,0.3)' },
  rejected: { color: '#ff6666', bg: 'rgba(200,50,50,0.12)', border: 'rgba(200,50,50,0.3)' },
};

const FIELD_LABELS = {
  name: 'Name',
  mobile: 'Mobile',
  email: 'Email',
  country: 'Country',
  dateOfBirth: 'Date of Birth',
  address: 'Address',
  usdtWalletAddress: 'USDT Wallet Address',
};

const normalize = (field, val) => {
  if (val === undefined || val === null) return '';
  if (field === 'dateOfBirth') return String(val).split('T')[0];
  return String(val).trim();
};

// Compares the requested values on a request against the user's current profile,
// returning only the fields that actually differ (requests always carry the full form,
// not just edited fields).
const getChangedFields = (request, currentUser) => {
  if (!currentUser) return [];
  return Object.keys(FIELD_LABELS)
    .map((field) => ({
      field,
      label: FIELD_LABELS[field],
      oldValue: currentUser[field],
      newValue: request[field],
    }))
    .filter((c) => normalize(c.field, c.oldValue) !== normalize(c.field, c.newValue));
};

const AdminProfileRequests = () => {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setLoading(true);
    getAdminProfileUpdateRequests({ page, limit: 20, status, search })
      .then((res) => { setRequests(res.data.requests); setTotal(res.data.total); setPages(res.data.pages); })
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [page, status, search]);

  useEffect(() => { load(); }, [load]);

  const openEditModal = async (requestId) => {
    try {
      const res = await getAdminProfileUpdateRequest(requestId);
      const req = res.data.request;
      setSelectedRequest(req);
      // Populate edit form with requested changes
      setEditData({
        name: req.name || '',
        mobile: req.mobile || '',
        email: req.email || '',
        country: req.country || '',
        dateOfBirth: req.dateOfBirth || '',
        address: req.address || '',
        usdtWalletAddress: req.usdtWalletAddress || '',
      });
      setEditModal(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load request');
    }
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    if (!window.confirm('Approve this profile update?')) return;

    setIsSubmitting(true);
    try {
      await approveProfileUpdateRequest(selectedRequest._id, { changes: editData });
      alert('Profile update approved!');
      setEditModal(false);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await rejectProfileUpdateRequest(rejectModal, { reason: rejectReason });
      alert('Profile update request rejected');
      setRejectModal(null);
      setRejectReason('');
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Profile Edit Requests</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>{total} total requests</div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {/* Search */}
        <div>
          <input
            type="text"
            placeholder="Search by user code or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: 'rgba(0,229,255,0.04)',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '10px 14px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        {/* Status Filter */}
        <div style={{ maxWidth: 300 }}>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Status</label>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: '#1a1a1a',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All Status</option>
            <option value="pending" style={{ background: '#1a1a1a', color: '#ffaa44' }}>Pending</option>
            <option value="approved" style={{ background: '#1a1a1a', color: '#66ff99' }}>Approved</option>
            <option value="rejected" style={{ background: '#1a1a1a', color: '#ff6666' }}>Rejected</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '1000px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
              {['User', 'Email', 'Mobile', 'Changes', 'Status', 'Requested At', 'Actions'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={7} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No profile requests found.</td></tr>
            ) : requests.map((r) => {
              const ss = statusStyle[r.status] || statusStyle.pending;
              const changes = getChangedFields(r, r.user);
              return (
                <tr key={r._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 13 }}>{r.user?.name || '—'}</div>
                    <div style={{ color: '#555', fontSize: 11 }}>{r.user?.userCode || '—'}</div>
                  </td>
                  <td style={{ color: '#888', fontSize: 11, padding: '11px 14px' }}>
                    {r.email || r.user?.email || '—'}
                  </td>
                  <td style={{ color: '#888', fontSize: 11, padding: '11px 14px' }}>
                    {r.mobile || r.user?.mobile || '—'}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    {changes.length === 0 ? (
                      <span style={{ color: '#555', fontSize: 11 }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 }}>
                        {changes.map((c) => (
                          <span key={c.field} style={{ background: 'rgba(102,170,255,0.12)', border: '1px solid rgba(102,170,255,0.3)', color: '#66aaff', borderRadius: 5, padding: '2px 6px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {c.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: ss.bg, border: `1px solid ${ss.border}`, color: ss.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
                      {r.status}
                    </span>
                  </td>
                  <td style={{ color: '#555', fontSize: 11, padding: '11px 14px', whiteSpace: 'nowrap' }}>
                    {fmtIST(r.createdAt)}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    {r.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEditModal(r._id)}
                          style={{ background: 'rgba(102,170,255,0.12)', border: '1px solid rgba(102,170,255,0.3)', color: '#66aaff', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          Edit & Approve
                        </button>
                        <button onClick={() => setRejectModal(r._id)}
                          style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: '#555', fontSize: 11 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Edit & Approve Modal */}
      {editModal && selectedRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, overflowY: 'auto', padding: '20px' }}>
          <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 600 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Edit & Approve Profile</div>
            <div style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>
              User: <span style={{ color: '#00e5ff' }}>{selectedRequest.userId?.name}</span>
            </div>

            {(() => {
              const changes = getChangedFields(selectedRequest, selectedRequest.userId);
              return changes.length === 0 ? null : (
                <div style={{ background: 'rgba(102,170,255,0.06)', border: '1px solid rgba(102,170,255,0.2)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                  <div style={{ color: '#66aaff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                    Requested Changes ({changes.length})
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {changes.map((c) => (
                      <div key={c.field} style={{ fontSize: 12 }}>
                        <span style={{ color: '#888', fontWeight: 600 }}>{c.label}: </span>
                        <span style={{ color: '#ff8888', textDecoration: 'line-through' }}>{normalize(c.field, c.oldValue) || '(empty)'}</span>
                        <span style={{ color: '#555', margin: '0 6px' }}>→</span>
                        <span style={{ color: '#66ff99', fontWeight: 600 }}>{normalize(c.field, c.newValue) || '(empty)'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <form onSubmit={handleApprove} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxHeight: '60vh', overflowY: 'auto', paddingRight: '8px' }}>
              {/* Name */}
              <div>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Name</label>
                <input type="text" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }} />
              </div>

              {/* Mobile */}
              <div>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Mobile</label>
                <input type="text" value={editData.mobile} onChange={(e) => setEditData({ ...editData, mobile: e.target.value })}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }} />
              </div>

              {/* Email */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Email</label>
                <input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }} />
              </div>

              {/* Country */}
              <div>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Country</label>
                <input type="text" value={editData.country} onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }} />
              </div>

              {/* DOB */}
              <div>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Date of Birth</label>
                <input type="date" value={editData.dateOfBirth?.split('T')[0] || ''} onChange={(e) => setEditData({ ...editData, dateOfBirth: e.target.value })}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }} />
              </div>

              {/* Address */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Address</label>
                <input type="text" value={editData.address} onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none' }} />
              </div>

              {/* USDT */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>USDT Wallet Address</label>
                <input type="text" value={editData.usdtWalletAddress} onChange={(e) => setEditData({ ...editData, usdtWalletAddress: e.target.value })}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'monospace', fontSize: '11px' }} />
              </div>

              {/* Buttons */}
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="button" onClick={() => setEditModal(false)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={isSubmitting} style={{ flex: 1, padding: '10px', background: isSubmitting ? 'rgba(255,255,255,0.06)' : 'rgba(102,255,153,0.2)', border: '1px solid rgba(102,255,153,0.4)', color: isSubmitting ? '#555' : '#66ff99', borderRadius: 8, fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Approving...' : 'Approve Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#111', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 14, padding: '28px', width: '100%', maxWidth: 380 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Reject Profile Update</div>
            <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>User will be notified about the rejection.</div>
            <form onSubmit={handleReject}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Reason (Optional)</label>
                <textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter rejection reason..."
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, outline: 'none', resize: 'none', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setRejectModal(null)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={isSubmitting} style={{ flex: 1, padding: '10px', background: isSubmitting ? 'rgba(255,255,255,0.06)' : 'rgba(200,50,50,0.2)', border: '1px solid rgba(200,50,50,0.4)', color: isSubmitting ? '#555' : '#ff6666', borderRadius: 8, fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfileRequests;
